import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { LogOut, LayoutDashboard, Settings, Menu, X, Layers } from 'lucide-react';

const Navbar = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const { user, profile, signOut, loading } = useAuth();

  const [menuOpen, setMenu]     = useState(false);
  const [signingOut, setSO]     = useState(false);
  const [dropOpen, setDropOpen] = useState(false);

  const dropRef = useRef(null);

  useEffect(() => { setMenu(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSignOut = async () => {
    setSO(true); setDropOpen(false);
    try { await signOut(); navigate('/'); }
    finally { setSO(false); }
  };

  const initials = profile?.full_name?.trim()
    ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? '?';

  if (loading) return (
    <header className="sticky top-0 z-50 bg-white dark:bg-stone-950 border-b border-stone-100 dark:border-stone-900">
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo.svg" alt="Castle Atlas" style={{ height: '28px', width: 'auto' }} className="dark:brightness-0 dark:invert" />
          <span className="text-sm font-bold text-stone-900 dark:text-white tracking-tight">Castle Atlas</span>
        </Link>
      </div>
    </header>
  );

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-stone-950 border-b border-stone-100 dark:border-stone-900" style={{ backdropFilter: 'blur(8px)' }}>
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">

        <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
          <img src="/logo.svg" alt="Castle Atlas" style={{ height: '28px', width: 'auto' }} className="dark:brightness-0 dark:invert" />
          <span className="text-sm font-bold text-stone-900 dark:text-white tracking-tight">Castle Atlas</span>
        </Link>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {/* User dropdown — desktop */}
              <div className="hidden md:block relative" ref={dropRef}>
                <button
                  onClick={() => setDropOpen(d => !d)}
                  aria-label="User menu"
                  aria-expanded={dropOpen}
                  className="w-8 h-8 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center hover:ring-2 hover:ring-brand-400 transition-all"
                >
                  <span className="text-xs font-bold text-stone-600 dark:text-stone-300">{initials}</span>
                </button>

                {dropOpen && (
                  <div className="absolute right-0 top-10 w-52 bg-white dark:bg-stone-900 rounded-2xl border border-stone-100 dark:border-stone-800 shadow-xl py-1 animate-scale-in">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-stone-100 dark:border-stone-800">
                      <p className="text-sm font-bold text-stone-900 dark:text-stone-100 truncate">{profile?.full_name || 'Your account'}</p>
                      <p className="text-xs text-stone-400 truncate mt-0.5">{user?.email}</p>
                    </div>

                    <Link
                      to="/dashboard"
                      onClick={() => setDropOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                    >
                      <LayoutDashboard className="w-3.5 h-3.5" />
                      Dashboard
                    </Link>

                    <Link
                      to="/skills"
                      onClick={() => setDropOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                    >
                      <Layers className="w-3.5 h-3.5" />
                      Skills
                    </Link>

                    <Link
                      to="/settings"
                      onClick={() => setDropOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      Settings
                    </Link>

                    <button
                      onClick={handleSignOut}
                      disabled={signingOut}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-stone-100 dark:border-stone-800 mt-1"
                    >
                      {signingOut
                        ? <div className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                        : <LogOut className="w-3.5 h-3.5" />
                      }
                      Sign out
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile hamburger */}
              <button className="md:hidden p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors" onClick={() => setMenu(m => !m)} aria-label="Toggle menu" aria-expanded={menuOpen}>
                {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              {!location.pathname.startsWith('/signup') && !location.pathname.startsWith('/login') && (
                <Link to="/login" className="btn-ghost text-sm px-3 py-1.5">Sign in</Link>
              )}
              {!location.pathname.startsWith('/login') && !location.pathname.startsWith('/signup') && (
                <Link to="/signup" className="btn-primary text-sm px-4 py-2">Get started</Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {user && menuOpen && (
        <nav aria-label="Mobile navigation" className="md:hidden border-t border-stone-100 dark:border-stone-900 bg-white dark:bg-stone-950 animate-fade-in">
          <div className="max-w-6xl mx-auto px-6 py-3 space-y-1">
            <Link to="/dashboard" onClick={() => setMenu(false)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold
                ${location.pathname.startsWith('/dashboard')
                  ? 'bg-orange-50 dark:bg-orange-900/10 text-brand-600'
                  : 'text-stone-600 dark:text-stone-400'}`}>
              <LayoutDashboard className="w-4 h-4" />Dashboard
            </Link>

            <Link to="/skills" onClick={() => setMenu(false)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold
                ${location.pathname.startsWith('/skills')
                  ? 'bg-orange-50 dark:bg-orange-900/10 text-brand-600'
                  : 'text-stone-600 dark:text-stone-400'}`}>
              <Layers className="w-4 h-4" />Skills
            </Link>

            <Link to="/settings" onClick={() => setMenu(false)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold
                ${location.pathname.startsWith('/settings')
                  ? 'bg-orange-50 dark:bg-orange-900/10 text-brand-600'
                  : 'text-stone-600 dark:text-stone-400'}`}>
              <Settings className="w-4 h-4" />Settings
            </Link>

            <button onClick={handleSignOut}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-500 w-full">
              <LogOut className="w-4 h-4" />Sign out
            </button>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;